import { React, useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/EditDevice.css';
import { editDeviceFromServer } from '../../redux/actions/devices.action';
import CustomButton from '../CustomElements/CustomButton';
import CustomTitle from '../CustomElements/CustomTitle';
import CustomContainer from '../CustomElements/CustomContainer';
import FormContainer from '../CustomElements/FormContainer';
import Selector from '../CustomElements/Selector';
import DateSelector from '../CustomElements/DateSelector';
import PhoneInput from '../CustomElements/PhoneInput';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
// Кастомные хуки
import { useButtonValidation } from '../../customHook/useButtonValidation';
import useFormDataChanged from '../../customHook/useFormDataChanged';
// Утилиты
import { handleUniversalChange } from '../../utils/formUtils';
import { omitFields } from '../../utils/omitFields';
import { convertToDate, transformFieldsToMoscowStart } from '../../utils/convertToDate';
import { prepareObjectForSubmit } from '../../utils/prepareObjectForSubmit';
import { validateSimNumbers } from '../../utils/validateSimNumbers';
// Действия Redux
import { addError } from '../../redux/actions/error.action';
import { load, remove } from '../../redux/actions/loader.action';
import { initUippsAndroidIdFromServer } from '../../redux/actions/uipps.action';
import { initIurAndroidIdFromServer } from '../../redux/actions/devices.action';

// Этот компонент отображает форму для изменения данных устройства

function EditDevice({ closeModalDevice, currentDevice, typeUipps, t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();

  // Получаем опции
  const optionsForSelect = useSelector((state) => state.options);

  // Получаем данные о процессе
  const loader = useSelector((state) => state.loader);

  // Получение сообщения об ошибке из глобального состояния
  const error = useSelector((state) => state.error);

  // Состояния для кнопки и уведомления об успешном добавлении
  const [editSuccess, setEditSuccess] = useState(false);

  // Состояние для формы
  const initialFormData = useMemo(
    () => ({
      deviceId: currentDevice?.id,
      sim1: currentDevice?.sim1 || '',
      manufactureDate: convertToDate(currentDevice.manufactureDate) || '',
      sim2: currentDevice?.sim2 || '',
      androidId: currentDevice?.androidId || '',
    }),
    [currentDevice]
  );

  const [formData, setFormData] = useState(initialFormData);

  //проверяем были ли изменения в форме
  const isFormEdit = useFormDataChanged(initialFormData, formData, true);

  // Вычисляем optionalFields на основе formData.deviceId
  const optionalFields = useMemo(() => {
    if (formData.deviceId >= 300000 && formData.deviceId <= 399999) {
      return ['sim1', 'sim2', 'androidId'];
    }
    return ['sim1', 'sim2'];
  }, [formData.deviceId]);

  //Функция для проверки валидности формы
  const isButtonDisabled = useButtonValidation(formData, optionalFields);
  // Обработчик отправки формы
  const handlerSubmit = (event) => {
    event.preventDefault();
    // сравниваем начальное и текущее состояние заменяем на null пустые строки для удаленных полей
    const formDataWithEmptyStrings = prepareObjectForSubmit(initialFormData, formData);
    // Удаляем поля, которые не нужно отправлять
    const formWithoutExcluded = omitFields(formDataWithEmptyStrings, ['deviceId']);
    // Преобразуем указанные поля в начало дня по Москве (если значение есть и валидное)
    const payload = transformFieldsToMoscowStart(formWithoutExcluded, ['manufactureDate']);
    dispatch(load());
    dispatch(editDeviceFromServer(payload, formData.deviceId, typeUipps));
  };

  // Закрывает модальное окно после успешного изменения устройства
  const handlerCloseModal = () => {
    closeModalDevice();
    setEditSuccess(false);
  };

  // Функция для обработки клика по кнопке открывает модальное окно подтверждения
  // и проверяет валидность SIM-карт
  const handleButtonClick = (event) => {
    event.preventDefault();
    const checkSim = validateSimNumbers(formData, setFormData);
    if (checkSim) {
      dispatch(addError({ error: checkSim }));
    } else if (!checkSim) {
      setEditSuccess(!editSuccess);
    }
  };

  //отслеживаем выполнение запроса
  useEffect(() => {
    // Если нет ошибок, закрываем модальное окно
    if (!loader?.isLoading && loader?.isSuccess) {
      //Меняем флаг для отображения сообщения что устройство изменено
      handlerCloseModal();
    }
    // при закрытии компонента чистим стейт загрузки
    return () => {
      dispatch(remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loader?.isLoading, loader?.isSuccess, error]);

  // отправляем запрос на получений списка androidId и документов при открытии формы
  useEffect(() => {
    if (typeUipps) {
      dispatch(initUippsAndroidIdFromServer());
    } else {
      dispatch(initIurAndroidIdFromServer());
    }
  }, [dispatch, typeUipps]);

  console.log(isButtonDisabled, !isFormEdit, optionalFields);

  return (
    <CustomContainer height="100%">
      <CustomTitle
        titleText={t?.editDeviceTitle || 'Изменение данных'}
        deviceType={currentDevice.type}
        deviceId={currentDevice.id}
      />
      {editSuccess && (
        <ConfirmationPage
          confirmText={t?.confirmEditDeviceText || 'Вы уверены, что хотите изменить данные устройства?'}
          confirmButtonText={t?.confirmButton || 'Изменить'}
          cancelButtonText={t?.cancelButton || 'Отменить'}
          onConfirm={handlerSubmit} // Функция, которая должна быть вызвана при подтверждении
          onCancel={handlerCloseModal} // Функция для закрытия модального окна или отмены действия
        />
      )}
      {!editSuccess && (
        <>
          <FormContainer>
            <PhoneInput
              column="left"
              label={t?.labelSim1 || 'SIM1'}
              name="sim1"
              onChange={(event) => handleUniversalChange(formData, setFormData, event)}
              value={formData.sim1}
            />
            <DateSelector
              column="left"
              width="100%"
              height="var(--sadr-font-size-60)"
              label={t?.labelManufactureDate || 'Дата производства *'}
              selectedDate={formData.manufactureDate}
              setSelectedDate={(date) =>
                handleUniversalChange(formData, setFormData, null, 'manufactureDate', date, 'date')
              }
              name="manufactureDate"
              placeholder={t?.placeholderManufactureDate || 'Выберите дату производства'}
              maxDate={new Date()}
            />
            <PhoneInput
              column="right"
              label={t?.labelSim2 || 'SIM2'}
              name="sim2"
              onChange={(event) => handleUniversalChange(formData, setFormData, event)}
              value={formData.sim2}
            />
            {![3, 2].includes(currentDevice?.typeId) && (
              <Selector
                column="right"
                options={typeUipps ? optionsForSelect?.uippsAndroidId : optionsForSelect?.iurAndroidId}
                value={
                  optionsForSelect?.[typeUipps ? 'uippsAndroidId' : 'iurAndroidId']?.find(
                    (option) => option.value === formData.androidId
                  ) || { value: formData.androidId, label: formData.androidId } ||
                  ''
                }
                onChange={(selectedOption) =>
                  handleUniversalChange(formData, setFormData, null, 'androidId', selectedOption, 'select')
                }
                placeholder={t?.placeholderSelectAndroidId || 'Выберите android_id'}
                label={t?.labelAndroidId || 'ANDROID_ID *'}
                width="100%"
                height="var(--sadr-font-size-60)"
                isMulti={false}
                name="androidId"
                optionText={
                  typeUipps
                    ? t?.textNoUnauthorizedUipps || 'Нет неавторизованных УИППС'
                    : t?.textNoUnauthorizedIur || 'Нет неавторизованных ИУР'
                }
              />
            )}
          </FormContainer>
          <CustomButton
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            text={t?.buttonSaveChanges || 'Сохранить изменения'}
            disabled={isButtonDisabled || !isFormEdit}
            onClick={handleButtonClick}
            isButton
          />
        </>
      )}
    </CustomContainer>
  );
}
export default EditDevice;
